import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock3, Eye, Image as ImageIcon, ImagePlus, LayoutTemplate, Monitor, Palette, RotateCcw, Save, Settings2, Sparkles, Trash2, Type, Upload } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, type MenuItem, type Site } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { Select } from '../components/Select';
import { ErrorBox, PageHeader, Toggle } from '../components/ui';
import { fullDesign, HomePreview, orderedItems } from './HomeDesignerPage';

interface BrandingForm {
  hotelLogoAssetId: string | null;
  hotelLogoUrl: string | null;
  homeBackgroundAssetId: string | null;
  homeBackgroundUrl: string | null;
  accentColor: string;
  welcomeHeadline: string;
  welcomeSubline: string;
  hour12: boolean;
  showClock: boolean;
  showDate: boolean;
  showRoom: boolean;
}

const SWATCHES = ['#6E4BFF', '#2563EB', '#0EA5E9', '#14B8A6', '#16A34A', '#CA8A04', '#EA580C', '#DC2626', '#DB2777', '#9F1239', '#B08D57', '#475569'];

export function BrandingPage() {
  const site = useSite();
  return <NeedsSite>{site ? <BrandingEditor key={site.id} site={site} /> : <PageHeader title="Welcome & Branding" />}</NeedsSite>;
}

function StatCard({ icon, title, sub, aside }: { icon: ReactNode; title: string; sub: string; aside: ReactNode }) {
  return (
    <div className="stat-tile">
      <span className="stat-tile-icon violet">{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="stat-tile-label" style={{ color: 'var(--ink)', fontWeight: 700 }}>
          {title}
        </span>
        <span className="stat-tile-sub">{sub}</span>
      </span>
      {aside}
    </div>
  );
}

function Section({ icon, title, sub, children }: { icon: ReactNode; title: string; sub?: string; children: ReactNode }) {
  return (
    <div className="brand-section">
      <span className="brand-section-icon">{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="brand-section-title">{title}</div>
        {sub && <div className="muted brand-section-sub">{sub}</div>}
        {children}
      </div>
    </div>
  );
}

/** Thumbnail with Upload file / Remove, as in the mockup. */
function AssetRow({ url, hint, wide, disabled, onChange }: { url: string | null; hint: string; wide?: boolean; disabled: boolean; onChange: (a: { id: string; url: string } | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useMutation({ mutationFn: (f: File) => api.upload<{ id: string; url: string }>('/assets', f), onSuccess: onChange });
  return (
    <>
      <div className="asset-row">
        <div className={`asset-thumb${wide ? ' wide' : ''}`}>{url ? <img src={url} alt="" style={{ objectFit: wide ? 'cover' : 'contain' }} /> : <ImagePlus size={22} />}</div>
        <div>
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn btn-soft" disabled={disabled || upload.isPending} onClick={() => input.current?.click()}>
              <Upload size={16} /> {upload.isPending ? 'Uploading…' : 'Upload file'}
            </button>
            {url && (
              <button type="button" className="btn" disabled={disabled} onClick={() => onChange(null)}>
                <Trash2 size={16} /> Remove
              </button>
            )}
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            {hint}
          </div>
        </div>
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
            e.target.value = '';
          }}
        />
      </div>
      <ErrorBox error={upload.error} />
    </>
  );
}

function BrandingEditor({ site }: { site: Site }) {
  const { can } = useSession();
  const qc = useQueryClient();
  // Resolve stored asset ids to URLs via the asset list.
  const assets = useQuery({ queryKey: ['assets'], queryFn: () => api.get<{ items: { id: string; url: string }[] }>('/assets') });
  // The preview draws the saved home design (default one) with the buttons in its order.
  const menu = useQuery({ queryKey: ['menu', site.id], queryFn: () => api.get<{ items: MenuItem[] }>(`/sites/${site.id}/menu`) });
  const b = site.branding as Record<string, string | boolean | null | undefined>;
  const urlOf = (id: unknown) => (id ? (assets.data?.items.find((a) => a.id === id)?.url ?? null) : null);
  const [form, setForm] = useState<BrandingForm | null>(null);
  const f: BrandingForm = form ?? {
    hotelLogoAssetId: (b.hotelLogoAssetId as string) ?? null,
    hotelLogoUrl: urlOf(b.hotelLogoAssetId),
    homeBackgroundAssetId: (b.homeBackgroundAssetId as string) ?? null,
    homeBackgroundUrl: urlOf(b.homeBackgroundAssetId),
    accentColor: (b.accentColor as string) ?? '#6E4BFF',
    welcomeHeadline: (b.welcomeHeadline as string) ?? 'Welcome',
    welcomeSubline: (b.welcomeSubline as string) ?? `We wish you a pleasant stay at ${site.name}.`,
    hour12: (b.hour12 as boolean) ?? false,
    showClock: (b.showClock as boolean) ?? true,
    showDate: (b.showDate as boolean) ?? true,
    showRoom: (b.showRoom as boolean) ?? true,
  };
  const set = (patch: Partial<BrandingForm>) => setForm({ ...f, ...patch });
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/sites/${site.id}`, {
        branding: {
          hotelLogoAssetId: f.hotelLogoAssetId,
          homeBackgroundAssetId: f.homeBackgroundAssetId,
          accentColor: f.accentColor,
          welcomeHeadline: f.welcomeHeadline,
          welcomeSubline: f.welcomeSubline,
          hour12: f.hour12,
          showClock: f.showClock,
          showDate: f.showDate,
          showRoom: f.showRoom,
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sites'] });
      setForm(null);
    },
  });
  const editable = can('ADMIN');
  const validColor = /^#[0-9A-F]{6}$/i.test(f.accentColor);
  const both = !!f.hotelLogoAssetId && !!f.homeBackgroundAssetId;
  const design = fullDesign(b.home);

  return (
    <>
      <PageHeader
        title="Welcome & Branding"
        sub="What guests see on the TV: your hotel logo (never the PulseIPTV logo), welcome text, colours and clock."
        actions={
          editable && (
            <>
              <Link to="/experience/home-design" className="btn btn-soft">
                <LayoutTemplate size={16} /> Design the home screen
              </Link>
              {save.isSuccess && !form && <span className="badge ok">Published</span>}
              <button className="btn" disabled={!form} onClick={() => setForm(null)} title="Undo the changes you haven't saved">
                <RotateCcw size={16} /> Reset
              </button>
              <button className="btn primary" disabled={!form || save.isPending || !validColor} onClick={() => save.mutate()}>
                <Save size={16} /> Save &amp; publish
              </button>
            </>
          )
        }
      />
      <div className="stat-strip">
        <StatCard
          icon={<ImageIcon size={22} />}
          title="Brand assets"
          sub="Logo and background"
          aside={
            <span className={`badge ${both ? 'ok' : 'warn'}`} style={{ whiteSpace: 'nowrap' }}>
              {both ? (
                <>
                  <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Configured
                </>
              ) : f.hotelLogoAssetId || f.homeBackgroundAssetId ? (
                'Partly set'
              ) : (
                'Not set'
              )}
            </span>
          }
        />
        <StatCard icon={<Monitor size={22} />} title="Welcome screen" sub="See how it looks on TV" aside={<span className="badge brand">Live preview</span>} />
        <StatCard
          icon={<Clock3 size={22} />}
          title="Clock format"
          sub={f.hour12 ? '12-hour (2:28 PM)' : '24-hour (14:28)'}
          aside={<span className={`badge ${f.showClock ? 'info' : ''}`}>{f.showClock ? 'Shown' : 'Hidden'}</span>}
        />
        <StatCard
          icon={<Palette size={22} />}
          title="Accent colour"
          sub="Branding colour on TV"
          aside={
            <span className="color-chip">
              <span style={{ background: validColor ? f.accentColor : '#ccc' }} /> {f.accentColor}
            </span>
          }
        />
      </div>

      <div className="brand-layout">
        <div className="card">
          <div className="pkg-head">
            <span className="pkg-head-icon">
              <Settings2 size={22} />
            </span>
            <span>
              <span className="pkg-head-title">Branding settings</span>
              <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                Upload your assets and customise the welcome screen.
              </span>
            </span>
          </div>

          <Section icon={<ImageIcon size={17} />} title="Hotel logo" sub="Shown in the top-left corner on the TV.">
            <AssetRow
              url={f.hotelLogoUrl}
              hint="Transparent PNG, light-coloured (it sits on a dark photo)."
              disabled={!editable}
              onChange={(a) => set({ hotelLogoAssetId: a?.id ?? null, hotelLogoUrl: a?.url ?? null })}
            />
          </Section>
          <Section icon={<ImageIcon size={17} />} title="Home background" sub="1920 × 1080 photo of your property, shown on the welcome screen.">
            <AssetRow
              url={f.homeBackgroundUrl}
              wide
              hint="JPG or PNG, 1920 × 1080 (16:9)."
              disabled={!editable}
              onChange={(a) => set({ homeBackgroundAssetId: a?.id ?? null, homeBackgroundUrl: a?.url ?? null })}
            />
          </Section>
          <Section icon={<Type size={17} />} title="Welcome text" sub="The guest's name is added after the headline when a guest is checked in.">
            <label className="brand-label">Welcome headline</label>
            <input value={f.welcomeHeadline} maxLength={80} disabled={!editable} onChange={(e) => set({ welcomeHeadline: e.target.value })} />
            <label className="brand-label">Welcome line</label>
            <input value={f.welcomeSubline} maxLength={200} disabled={!editable} onChange={(e) => set({ welcomeSubline: e.target.value })} />
          </Section>
          <div className="brand-split">
            <Section icon={<Palette size={17} />} title="Accent colour" sub="Used for buttons and highlights on the TV.">
              <div className="row" style={{ alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'nowrap' }}>
                {/* The swatch opens the colour picker; the hex can also be typed. */}
                <label title="Pick a colour" className="color-well" style={{ background: validColor ? f.accentColor : '#ccc' }}>
                  <input type="color" disabled={!editable} value={validColor ? f.accentColor : '#6E4BFF'} onChange={(e) => set({ accentColor: e.target.value.toUpperCase() })} />
                </label>
                <input
                  className="mono"
                  value={f.accentColor}
                  maxLength={7}
                  disabled={!editable}
                  style={{ width: 110 }}
                  onChange={(e) => {
                    const v = e.target.value.trim().toUpperCase();
                    set({ accentColor: v.startsWith('#') ? v : `#${v}` });
                  }}
                />
              </div>
              <div className="row" style={{ gap: 6, marginTop: 10 }}>
                {SWATCHES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    title={c}
                    disabled={!editable}
                    onClick={() => set({ accentColor: c })}
                    className={`swatch${f.accentColor.toUpperCase() === c ? ' on' : ''}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </Section>
            <Section icon={<Clock3 size={17} />} title="Clock format" sub="Time format shown on the TV.">
              <div style={{ marginTop: 10 }}>
                <Select value={f.hour12 ? '12' : '24'} disabled={!editable} onChange={(e) => set({ hour12: e.target.value === '12' })} aria-label="Clock format">
                  <option value="24">24-hour (14:28)</option>
                  <option value="12">12-hour (2:28 PM)</option>
                </Select>
              </div>
            </Section>
          </div>
          <Section icon={<Eye size={17} />} title="Display options" sub="What the top-right corner of the TV shows.">
            <div className="row" style={{ gap: 28, marginTop: 12 }}>
              {(
                [
                  ['showClock', 'Show clock'],
                  ['showRoom', 'Show room number'],
                  ['showDate', 'Show date'],
                ] as const
              ).map(([k, label]) => (
                <span key={k} className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                  <Toggle on={f[k]} onChange={(v) => editable && set({ [k]: v })} label={label} />
                  <span style={{ fontWeight: 600 }}>{label}</span>
                </span>
              ))}
            </div>
          </Section>
          <ErrorBox error={save.error} />
        </div>

        <div className="stack brand-side">
          <div className="card">
            <div className="pkg-head">
              <span className="pkg-head-icon">
                <Eye size={22} />
              </span>
              <span style={{ flex: 1 }}>
                <span className="pkg-head-title">Live preview</span>
                <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                  The saved home design (all rooms) with these settings. Change the layout in the Home screen designer.
                </span>
              </span>
              <span className="badge">TV · 16:9</span>
            </div>
            <HomePreview
              look={{
                siteName: site.name,
                timezone: site.timezone,
                logoUrl: f.hotelLogoUrl,
                backgroundUrl: f.homeBackgroundUrl,
                headline: f.welcomeHeadline,
                subline: f.welcomeSubline,
                accent: validColor ? f.accentColor : '#6E4BFF',
                hour12: f.hour12,
                showRoom: f.showRoom,
                showClock: f.showClock,
                showDate: f.showDate,
              }}
              design={design}
              items={orderedItems(menu.data?.items ?? [], design).filter((m) => !design.hiddenKeys.includes(m.key))}
              urlOf={urlOf}
            />
          </div>
          <div className="tip-card">
            <Sparkles size={22} />
            <div>
              <strong>Tip</strong>
              <div>The preview is approximate. The TVs draw the approved layout, which can differ slightly by TV model and screen size.</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
